// The roster mirror: fetched with an ETag, stored encrypted under the device's mirror key.
import type { VavrDb } from "./db.ts";
import type { DeviceApi } from "./api.ts";

export interface Roster {
  version: number;
  site_id: string;
  rooms: { id: string; name: string; teacher_ids: string[] }[];
  children: { id: string; first: string; last: string; room_id: string | null; photo_sha256: string | null; status: string }[];
  adults: { id: string; first: string; last: string; photo_sha256: string | null; pin_hash: string | null }[];
  links: { child_id: string; adult_id: string; relation: string; kind: "guardian" | "authorised" | "delegate"; valid_from: number | null; valid_to: number | null }[];
  restrictions: { id: string; child_id: string; subject_adult_id: string | null; subject_name: string | null; kind: string; never_release: boolean; valid_to: number | null; notes: string | null }[];
  delegations: { id: string; child_ids: string[]; delegate_adult_id: string; valid_from: number; valid_to: number; code_hash: string | null }[];
  staff: { id: string; name: string; roles: string[]; pin_hash: string | null }[];
}

export async function refreshRoster(db: VavrDb, api: DeviceApi, mirrorKey: CryptoKey): Promise<{ changed: boolean; version: number | null }> {
  const current = await db.roster.get("roster");
  const res = await api.request("GET", "/api/v1/device/roster", { headers: current?.etag ? { "If-None-Match": current.etag } : {} });
  if (res.status === 304) return { changed: false, version: current?.version ?? null };
  if (!res.ok) throw new Error(`roster ${res.status}`);
  const roster = (await res.json()) as Roster;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, mirrorKey, new TextEncoder().encode(JSON.stringify(roster)));
  await db.roster.put({ key: "roster", version: roster.version, etag: res.headers.get("ETag"), iv, ct, fetched_at: Date.now() });
  return { changed: true, version: roster.version };
}

export async function readRoster(db: VavrDb, mirrorKey: CryptoKey): Promise<Roster | null> {
  const r = await db.roster.get("roster");
  if (!r) return null;
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: r.iv as BufferSource }, mirrorKey, r.ct);
  return JSON.parse(new TextDecoder().decode(pt)) as Roster;
}
